'use es6';

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Small } from 'foundations-components/transitional/components/Small';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import Immutable from 'immutable';
import MobileFilterBar from 'marketplace-ui-common/components/mobile/MobileFilterBar';
import MobileTagButton from 'marketplace-ui-common/components/mobile/MobileTagButton';
import PropTypes from 'prop-types';
import { Fragment, useCallback, useMemo } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import * as CustomPropTypes from '../../../constants/PropTypes';
import * as FilterKey from '../../../constants/search/FilterKey';
import { getTranslatedSortText } from '../../../utils/i18n';
import FilterTag from './FilterTag';
import FilterTagText from './FilterTagText';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ResultsFilters = ({
  filters,
  isMobile,
  onClearFilters,
  onFilterChange,
  onSortChange,
  startSlot,
  trackInteraction,
  validFilterTagKeys
}) => {
  const handleCloseFilterTag = useCallback((filterKey, value) => {
    const filterValue = filters.get(filterKey);
    onFilterChange(filterKey, Immutable.Set.isSet(filterValue) ? filterValue.delete(value) : null);
  }, [filters, onFilterChange]);
  const renderedTags = useMemo(() => {
    return validFilterTagKeys.reduce((final, key) => {
      let value = filters.get(key);
      if (!Immutable.Set.isSet(value)) {
        value = Immutable.Set.of(value);
      }
      value.forEach(val => {
        final.push( /*#__PURE__*/_jsx(FilterTag, {
          filterKey: key,
          onCloseClick: () => {
            handleCloseFilterTag(key, val);
            trackInteraction('close filter tag', {
              filter: key,
              value: val
            });
          },
          value: val
        }, `${key}:${val}`));
      });
      return final;
    }, []);
  }, [filters, handleCloseFilterTag, trackInteraction, validFilterTagKeys]);
  const renderedMobileTags = useMemo(() => {
    return validFilterTagKeys.reduce((final, key) => {
      let value = filters.get(key);
      if (!Immutable.Set.isSet(value)) {
        value = Immutable.Set.of(value);
      }
      value.forEach(val => {
        final.push( /*#__PURE__*/_jsx(MobileTagButton, {
          closeable: true,
          onClick: () => {
            handleCloseFilterTag(key, val);
            trackInteraction('close filter tag', {
              filter: key,
              value: val
            });
          },
          children: /*#__PURE__*/_jsx(FilterTagText, {
            filterKey: key,
            value: val
          })
        }, `${key}:${val}`));
      });
      return final;
    }, []);
  }, [filters, handleCloseFilterTag, trackInteraction, validFilterTagKeys]);
  const renderedDefaultFilterBar = useMemo(() => {
    return /*#__PURE__*/_jsxs(TFlex, {
      align: "center",
      wrap: "wrap",
      children: [renderedTags, /*#__PURE__*/_jsx(Button, {
        onClick: evt => {
          onClearFilters(evt);
          trackInteraction('clear all filters', {
            filterCount: renderedTags.length
          });
        },
        use: "link",
        marginInlineStart: "300",
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "appMarketplace.storefront.results.resultsPage.clearFiltersText"
          })
        })
      })]
    });
  }, [onClearFilters, trackInteraction, renderedTags]);
  const clearSort = useCallback((sortKey, sortOrder) => {
    onSortChange(sortKey, sortOrder);
  }, [onSortChange]);
  const sortTag = useMemo(() => {
    const sortKey = filters.get(FilterKey.SORT_FIELD);
    const sortOrder = filters.get(FilterKey.SORT_ORDER);
    const sortText = getTranslatedSortText(sortKey, sortOrder);
    const tagText = I18n.text(`appMarketplace.storefront.results.sort.filterTagPreface`, {
      sortText
    });
    return sortKey !== null && /*#__PURE__*/_jsx(MobileTagButton, {
      onClick: clearSort,
      closeable: true,
      children: tagText
    });
  }, [clearSort, filters]);
  if (!renderedTags.length && !isMobile) {
    return null;
  }
  return isMobile ? /*#__PURE__*/_jsx(MobileFilterBar, {
    startSlot: startSlot,
    middleSlot: /*#__PURE__*/_jsxs(Fragment, {
      children: [renderedMobileTags, sortTag]
    })
  }) : /*#__PURE__*/_jsx(TFlex, {
    align: "center",
    wrap: "wrap",
    children: renderedDefaultFilterBar
  });
};
ResultsFilters.propTypes = {
  filters: CustomPropTypes.storefrontFilters.isRequired,
  isMobile: PropTypes.bool,
  onClearFilters: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onSortChange: PropTypes.func,
  sortOnly: PropTypes.bool,
  startSlot: PropTypes.node,
  trackInteraction: PropTypes.func.isRequired,
  validFilterTagKeys: ImmutablePropTypes.orderedSetOf(PropTypes.oneOf(Object.values(FilterKey))).isRequired
};
export default ResultsFilters;